/**
 * Request a password reset.
 *
 * The response is identical whether or not the email is registered, so this
 * page cannot be used to discover who has an account. Outside production the
 * API returns the reset link directly (there is no mail transport in this
 * MVP), and it is shown here so the flow is actually testable.
 */
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, ErrorAlert, Input } from '../../components/index.js';
import { authApi } from '../../services/index.js';
import { useMutation } from '../../hooks/useApi.js';

export function ForgotPasswordPage() {
  const { t } = useTranslation('administration');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(null);

  const { mutate, pending, error, fieldErrors } = useMutation(async () => {
    const result = await authApi.forgotPassword(email);
    setSent(result ?? {});
  });

  if (sent) {
    return (
      <Card>
        <div className="stack">
          <div>
            <h1 className="auth-title">{t('auth.forgot.sentTitle')}</h1>
            <p className="auth-subtitle">
              <Trans t={t} i18nKey="auth.forgot.sentMessage" values={{ email }} components={{ strong: <strong /> }} />
            </p>
          </div>

          {sent.devResetUrl && (
            <Alert variant="warning" title={t('auth.forgot.devTitle')}>
              {t('auth.forgot.devMessage')}{' '}
              <Link to={`/reset-password?token=${sent.devResetToken}`}>{t('auth.forgot.devLink')}</Link>
            </Alert>
          )}

          <Link to="/login" className="btn btn-block">
            {t('auth.backToSignIn')}
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutate().catch(() => {});
        }}
        noValidate
      >
        <div>
          <h1 className="auth-title">{t('auth.forgot.title')}</h1>
          <p className="auth-subtitle">{t('auth.forgot.subtitle')}</p>
        </div>

        <ErrorAlert error={error} />

        <Input
          label={t('common:fields.email')}
          type="email"
          name="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          error={fieldErrors.email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Button type="submit" variant="primary" size="lg" block loading={pending}>
          {t('auth.forgot.submit')}
        </Button>

        <p className="auth-footer">
          <Link to="/login">{t('auth.backToSignIn')}</Link>
        </p>
      </form>
    </Card>
  );
}
